import {useTranslation} from 'react-i18next';
import {SVG} from '../../../../../../constants/assetLists';
import {useMemo, useState} from 'react';
import useScreenSize from '../../../../../../hooks/useScreenSize';
import AppButton from '../../../../../../components/common/AppButton';
import AppTextInput from '../../../../../../components/common/AppTextInput';
import AddUsers from './components/AddUsers';
import MenuSection from './components/MenuSection';
import {useUsersContext} from '../../../../../../context/users/UsersContextProvider';
import {useSnackbar} from '../../../../../../context/SnackbarContextProvider';
import {useMutation} from '@tanstack/react-query';
import {handleDeleteBulkUserService} from '../../../../../../services/users';
import AppConfirmationDialog from '../../../../../../components/common/AppConfirmationDialog';
import QUERY_KEYS from '../../../../../../constants/queryKeys';

function Header() {
  const {i18n} = useTranslation();
  const isHebrew = useMemo(() => i18n.language === 'heb', [i18n.language]);

  const {
    allUsers,
    setSelectedUser,
    setShowAddEditDialog,
    selectedUsers,
    setSelectedUsers,
    searchText,
    setSearchText,
    refetchAllUsers,
  } = useUsersContext();

  const screenSize = useScreenSize();
  const isLarge = screenSize == 'xl' || screenSize == '2xl';

  const {showSnackbar} = useSnackbar();
  const isOneSelected = selectedUsers.length === 1;
  const disableDelete = !(selectedUsers.length >= 1);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleModalOpen = () => {
    setSelectedUser(selectedUsers[0]);
    setSelectedUsers([]);

    setShowAddEditDialog(true);
  };

  const handleConfirmationOpenClose = () => {
    setShowDeleteConfirmation(!showDeleteConfirmation);
  };

  const handleOnConfirm = () => {
    handleDeleteRole(selectedUsers as string[]);
    handleConfirmationOpenClose();
  };

  const {mutate: handleDeleteRole} = useMutation({
    mutationFn: (id: string[]) => handleDeleteBulkUserService({users: id}),
    onSuccess: data => {
      if (data?.data?.successful) {
        const msg =
          selectedUsers?.length === 1
            ? 'מחיקת המשתמש בוצעה בהצלחה'
            : 'מחיקה מרובה הושלמה';
        showSnackbar({
          message: msg,
          type: 'success',
        });
      }
      if (
        data?.data?.details?.announcementAssignees?.length ||
        data?.data?.details?.taskAssignees?.length
      ) {
        const userIds = [
          ...(data?.data?.details?.announcementAssignees || []),
          ...(data?.data?.details?.taskAssignees || []),
        ];
        const users =
          allUsers?.data?.filter(user => userIds.includes(user._id)) || [];
        const userNames = users
          .map(user => `${user.firstName} ${user.lastName}`)
          .join(', ');
        showSnackbar({
          message: `כדי למחוק משתמש/ים: ${userNames} הסר את ההקצאות שלו/שלה מכל המשימות וההודעות`,
          type: 'error',
        });
      }
      setSelectedUsers([]);
      refetchAllUsers();
    },
    onError: errors => {
      showSnackbar({
        message: errors?.message || 'מחיקת המשתמש/ים נכשלה, נסה שוב',
        type: 'error',
      });
    },
  });

  return (
    <>
      <div
        className={`w-full py-4 bg-white px-4 flex flex-wrap items-center justify-between gap-3 shadow-md z-2 ${
          isLarge ? (isHebrew ? 'rounded-tl-[32px]' : 'rounded-tr-[32px]') : ''
        }`}>
        <div className="flex-1 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <AddUsers />

            <AppButton
              variant="primary"
              py="12px"
              disabled={!isOneSelected}
              iconStart={
                <div className="w-[14px] h-[14px]">
                  <img
                    src={SVG.editPencilWhiteIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
              onClick={handleModalOpen}>
              עריכה
            </AppButton>

            <AppButton
              variant="primary"
              py="12px"
              disabled={disableDelete}
              iconStart={
                <div className={`w-[14px] h-[14px]`}>
                  <img
                    src={SVG.whiteCloseIcon}
                    alt="icon"
                    className={`w-full h-full object-contain`}
                  />
                </div>
              }
              onClick={handleConfirmationOpenClose}>
              מחיקה
            </AppButton>
          </div>

          <div className="flex items-center gap-3">
            <AppTextInput
              placeholder="חיפוש משתמש"
              value={searchText}
              onChange={handleSearch}
              containerClassName="!w-[499px]"
              className="bg-transparent !rounded-[28px] pl-16"
              endIcon={
                <div className="flex items-center gap-2">
                  {searchText && (
                    <div
                      onClick={() => setSearchText('')}
                      className="w-[24px] h-[24px] flex justify-center items-center rounded-full hover:bg-gray-200 cursor-pointer">
                      <img
                        src={SVG.closeDarkIcon}
                        alt="Clear"
                        className="w-[12px] h-[12px] object-contain"
                      />
                    </div>
                  )}
                  <div
                    className={`w-[24px] h-[24px] flex justify-center items-center rounded-full ${
                      searchText && 'bg-aqua'
                    }`}>
                    <img
                      src={SVG.searchIcon}
                      alt="Search"
                      className="w-[18px] h-[18px] object-contain"
                    />
                  </div>
                </div>
              }
            />

            <MenuSection />
          </div>
        </div>

        <div>
          <p className="m-0 text-black">
            {allUsers?.pagination?.total || 0} משתמשים
          </p>
        </div>
      </div>

      <AppConfirmationDialog
        open={showDeleteConfirmation}
        onClose={handleConfirmationOpenClose}
        onConfirm={handleOnConfirm}
        icon={
          <img
            src={SVG.deleteRedIcon}
            alt="icon"
            className="w-[48px] h-[48px] object-contain"
          />
        }
        title="שים לב"
        subTitle="האם אתה בטוח שברצונך למחוק?"
        confirmBtnText="כן"
        cancelBtnText="לא"
      />
    </>
  );
}

export default Header;
